import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';

import { ENVIRONMENT } from '../../environments/environment';
import CONFIGURATION from '../../configurations/configuration';

import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { EventEmitter } from '../models/generic.class';
import { memberCategoryCodes } from 'app/enum/member.enum';
import {
  MemberLoginDetail,
  MemberSubaccount,
  SubscriptionAgreementTemplate,
  SubscriptionAgreementTemplatev1,
  MemberContactChangeTemplate,
  CountryDetailData,
  MemberBankAccount,
  MemberBankAccountv1,
} from '../models/member.class';


@Injectable()
export class MemberService {
  myInfoData: any;
  blockInvestment = new BehaviorSubject<boolean>(false);
  reverificationReasons = new BehaviorSubject<any>(false);
  memberStatusEventEmitter: EventEmitter;
  constructor(
    private authService: AuthService,
    private baseService: BaseService,
    private http: HttpClient,
    private router: Router,
  ) {
    this.memberStatusEventEmitter = new EventEmitter();
  }

  setBlockInvestment(value: boolean): void {
    this.blockInvestment.next(value);
  }

  getBlockInvestment(): Observable<boolean> {
    return this.blockInvestment.asObservable();
  }

  setReverificationReasons(value: any): void {
    this.reverificationReasons.next(value);
  }

  getReverificationReasons(): Observable<any> {
    return this.reverificationReasons.asObservable();
  }

  activate(countryCode: string, userName: string, token: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/users/activate`;
    return this.baseService.put(url, {
      country_code: countryCode,
      username: userName,
      token: token
    });
  }

  addLead(body: any): Observable<any> {
    if (CONFIGURATION.country_code === 'MY') {
      body.contactNumber = '';
    } else {
      body.mobile_phone_number = '';
    }
    const url = ENVIRONMENT.service.v2 + '/leads';
    return this.baseService.post(url, body);
  }

  getLoginDetail(): Observable<MemberLoginDetail> {
    const url = `${ENVIRONMENT.service.v2}/me/login`;
    return this.baseService.get(url).pipe(
      map(this.mapLoginDetail));
  }

  updateEntityDetail(entityCode: string): Observable<any> {
    const body = {
      entity_code: entityCode
    };
    const url = `${ENVIRONMENT.service.v2}/me/entity`;
    return this.baseService.put(url, body);
  }

  getMemberDetail() {
    const url = ENVIRONMENT.service.v2 + '/me';
    return this.baseService.get(url);
  }

  getIndustriesMasterData(countryCode: string, type: string): Observable<any> {
    let url = ENVIRONMENT.service.v2 + '/master_data/industries/' + countryCode + '?type=' + type;
    return this.baseService.get(url);
  }

  getMemberActivationStepDetail() {
    const url = ENVIRONMENT.service.v2 + '/me?attributes=id,activation_step_id&includes=activation_step';
    return this.baseService.get(url);
  }

  getEmploymentStatuses(countryCode: string) {
    const languageHeader = this.baseService.getCustomXHeaderLanguageKey();
    const url = `${ENVIRONMENT.service.v2}/master_data/employment_statuses/${countryCode}`;
    return this.baseService.get(url, [languageHeader]);
  }

  createMemberSubscriptionAgreement(memberSubscriptionAgreements: any) {
    const url = `${ENVIRONMENT.service.v2}/me/subscription_agreements`;
    return this.baseService.post(url, memberSubscriptionAgreements);
  }

  getPlatformAgreementPDF(): Observable<any> {
    const token = this.authService.getBearerToken();
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + token
    });
    const url = `${ENVIRONMENT.service.v2}/me/subscription_agreements/download/latest`;
    return this.http.get(url, { headers: headers, responseType: 'blob' });
  }

  getSubscriptionAgreementTemplates(): Observable<Array<SubscriptionAgreementTemplate>> {
    const url = `${ENVIRONMENT.service.v2}/members/subscription_agreement_templates/${CONFIGURATION.country_code}`;
    return this.baseService.get(url).pipe(
      map(this.mapSubscriptionAgreementTemplate));
  }

  saveGenericQuestionnaire(genericQuestionnaire: any) {
    const url = `${ENVIRONMENT.service.v2}/me/generic_questionnaires`;
    return this.baseService.post(url, genericQuestionnaire);
  }

  saveSuitabilityAssessment(suitabilityAssessment: any) {
    const url = `${ENVIRONMENT.service.v2}/me/suitability_assessments`;
    return this.baseService.post(url, suitabilityAssessment);
  }

  sendActivationLink(userName: string, countryCode: string, token: string, completion: number, channel?: string): Observable<any> {
    const url = ENVIRONMENT.service.v2 + 'users/registration/stage';
    const currentUrl = this.router.url;
    return this.baseService.put(url, {
      completion: completion,
      countryId: countryCode,
      redirectLink: location.origin + '/activation',
      token: token,
      userName: userName,
      otpChannel: channel,
      templateId: currentUrl.indexOf("/investor") > -1 ?
        CONFIGURATION.sendgrid_template.investor_email_template_id : CONFIGURATION.sendgrid_template.borrower_email_template_id
    });
  }

  sendOtpCode(username: string, countryCode: string, token: string, stage: number, otpChannel: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/users/registration/stage`;
    const currentUrl = this.router.url;

    // If 2Fa enabled, then use 2Fa email
    // else use the old configuration
    let emailTemplateId = currentUrl.indexOf('/investor') > -1 ?
      CONFIGURATION.sendgrid_template.investor_email_template_id : CONFIGURATION.sendgrid_template.borrower_email_template_id;
    if (CONFIGURATION.enable2Fa) {
      emailTemplateId = currentUrl.indexOf('/investor') > -1 ?
        CONFIGURATION.sendgrid_template.investor_twoFa_email_template_id : CONFIGURATION.sendgrid_template.borrower_twoFa_email_template_id;
    }

    return this.baseService.put(url, {
      stage: stage,
      countryCode: countryCode,
      otp_channel: otpChannel,
      email: {
        redirectLink: location.origin + '/activation',
        send_name: CONFIGURATION.name,
        send_email: CONFIGURATION.email,
        template_id: emailTemplateId
      },
      token: token,
      username: username,
    });
  }

  signUp(body: any): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/users/registration';
    return this.baseService.post(url, body);
  }

  getMemberBankAccounts() {
    const url = `${ENVIRONMENT.service.v2}/me/bank_accounts`;
    return this.baseService.get(url);
  }

  getCompanyBankAccounts() {
    const url = `${ENVIRONMENT.service.v2}/members/company/bank_accounts`;
    return this.baseService.get(url);
  }

  addBankAccount(body: MemberBankAccount): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/me/bank_accounts`;
    return this.baseService.post(url, body);
  }

  updateBankAccount(memberBankAccount: MemberBankAccount, id: number): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/me/bank_accounts/${id}`;
    return this.baseService.put(url, memberBankAccount);
  }

  updateMember(body: any): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/me';
    return this.baseService.put(url, body);
  }

  updateMemberActivationStep(body: any) {
    const url = `${ENVIRONMENT.service.v2}/me/activation_step`;
    return this.baseService.put(url, body);
  }

  updateMemberPhoneNumber(body: MemberContactChangeTemplate) {
    const url = `${ENVIRONMENT.service.v2}/me/mobile_phone_number`;
    return this.baseService.put(url, this.baseService.toSnakeCaseRequestBody(body));
  }

  verifyMemberPhoneNumber(body: any) {
    const url = `${ENVIRONMENT.service.v2}/me/mobile_phone_number/verify`
    return this.baseService.put(url, this.baseService.toSnakeCaseRequestBody(body));
  }

  checkForgotPasswordRequest(token: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/members/password/check_reset_request?token=${token}`;
    return this.baseService.get(url);
  }

  forgotPassword(body: any): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/members/password/forgot`;
    return this.baseService.post(url, body);
  }

  resetPassword(body: any): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/members/password/reset';
    return this.baseService.put(url, body);
  }

  updatePassword(body: any) {
    const url = `${ENVIRONMENT.service.v2}/me/password`;
    return this.baseService.put(url, body);
  }

  updateSignUpInformation(userName: string, countryCode: string, token: string, member: any) {
    const url = ENVIRONMENT.service.member + ENVIRONMENT.service.public_identifier + '/member_auth';
    return this.baseService.put(url, {
      countryId: countryCode,
      member: member,
      token: token,
      userName: userName
    });
  }

  getMemberInfo(myInfoPayload: Object) {
    return this.baseService.post(`${ENVIRONMENT.service.member}/users/registration/gov_info`, myInfoPayload);
  }

  addReferralBorrower(referral: any, country_code: string, member_type_code: string): Observable<any> {
    const body = {
      "referral_company_name": referral.referralCompany,
      "referral_contact_name": referral.referralFullName,
      "referral_contact_number": referral.referralMobilePhoneNumber,
      "referral_contact_email": referral.referralEmail,
      "referee_company_name": referral.referrerCompany,
      "referee_contact_name": referral.referrerFullName,
      "referee_contact_number": referral.referrerMobilePhoneNumber,
      "referee_contact_email": referral.referrerEmail
    }
    const url = ENVIRONMENT.service.v2 + '/referrals/' + country_code + '/' + member_type_code + '/refer';
    return this.baseService.post(url, body);
  }

  signupReferralUpdates(data: any, country_code: string, member_type_code: string): Observable<any> {
    const body = {
      "email": data.email,
      "name": data.fullName
    }
    const url = ENVIRONMENT.service.v2 + '/referrals/' + country_code + '/' + member_type_code + '/register';
    return this.baseService.post(url, body);
  }

  getMemberLoyaltyDetail() {
    const url = ENVIRONMENT.service.loyalty + '/member_tiers/me';
    return this.baseService.get(url);
  }

  getLatestBlankSA(countryCode: string): Observable<any> {
    const token = this.authService.getBearerToken();
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + token
    });
    const url = `${ENVIRONMENT.service.v2}/members/subscription_agreement_templates/${countryCode}/blank`;
    return this.http.get(url, { headers: headers, responseType: 'blob' });
  }

  getCountryDetail(countryCode: string) {
    const url = `${ENVIRONMENT.service.v2}/master_data/countries/all/code/${countryCode}`;
    return this.baseService.get(url).pipe(
      map(this.mapCountryDetails)
    );
  }

  getAddressLocation(countryCode: string, key: string) {
    const url = ENVIRONMENT.service.v2 + '/master_data/locations/' + countryCode + '?search=' + key;
    return this.baseService.get(url);
  }

  getActivationSteps(countryCode: string) {
    const url = `${ENVIRONMENT.service.v2}/master_data/activation_steps/${countryCode}`;
    return this.baseService.get(url);
  }

  getMemberStatuses(countryCode: string) {
    const url = `${ENVIRONMENT.service.v2}/master_data/member_statuses/${countryCode}`;
    return this.baseService.get(url);
  }

  getIndustries(countryCode: string) {
    const url = `${ENVIRONMENT.service.v2}/master_data/industries/${countryCode}`;
    return this.baseService.get(url);
  }

  getAreaName(countryCode: string, areaId: string) {
    const url = ENVIRONMENT.service.v2 + '/master_data/locations/' + countryCode + '?ids=' + areaId;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getIncomeRange(countryCode: string): Observable<any> {
    return this.baseService.get(ENVIRONMENT.service.v2 + '/master_data/income_ranges/' + countryCode + '?order=income_range_type,ASC');
  }

  getProvinces(countryCode: string): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/master_data/provinces/' + countryCode;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getCities(countryCode: string, stateId: number): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/cities/${countryCode}?parent_id=${stateId}`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getDistricts(countryCode: string, cityId: number): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/districts/${countryCode}?parent_id=${cityId}`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getReligion(countryCode: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/religions/${countryCode}`;
    return this.baseService.get(url);
  }

  getMaritalStasuses(countryCode: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/marital_statuses/${countryCode}`;
    return this.baseService.get(url);
  }

  getBankAccountTypes(countryCode: string): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/master_data/bank_account_types/' + countryCode;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getCompanyEntityTypes(countryCode: string): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/master_data/company_entity_types/' + countryCode;
    return this.baseService.get(url);
  }

  getCompanyTotalEmployees(countryCode: string): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/master_data/total_employees/' + countryCode;
    return this.baseService.get(url);
  }

  getMemberEntityTypes(countryCode: string): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/master_data/member_entity_types/' + countryCode;
    return this.baseService.get(url);
  }

  getCountries() {
    const url = `${ENVIRONMENT.service.v2}/master_data/countries/all`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]).pipe(
      map(this.filterCountriesList));
  }

  getDegree(countryCode: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/degrees/${countryCode}`;
    return this.baseService.get(url);
  }

  getGenders(countryCode: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/genders/${countryCode}`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getCurrencies(): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/currencies/all`;
    return this.baseService.get(url);
  }

  getBanks(countryCode: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/banks/${countryCode}`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getAccreditedStatuses(countryCode: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/accredited_statuses/${countryCode}`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getIdentityCardTypes(countryCode: string, taxCardType: string = null): Observable<any> {
    let url = `${ENVIRONMENT.service.v2}/master_data/identity_card_types/${countryCode}`;
    if (taxCardType) {
      url = `${url}?search=${taxCardType}`;
    }
    return this.baseService.get(url);
  }

  getAddressFromOneMap(zipCode: string): Observable<any> {
    const url = 'https://developers.onemap.sg/commonapi/search?returnGeom=N&getAddrDetails=Y&searchVal=' + zipCode;
    return this.baseService.get(url);
  }

  getDocumentToSign(): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/me/kyc/documents_to_sign`;
    return this.baseService.get(url);
  }

  getPlatformAgreement(countryCode: string): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/master_data/subscription_agreement_templates/${countryCode}/code/latest`;
    return this.baseService.get(url);
  }

  signRiskDisclosure(): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/me/subscription_agreements/risk_disclosure/signoff`;
    return this.baseService.put(url, {});
  }

  getReverificationReason() {
    const url = `${ENVIRONMENT.service.v2}/me/reverification_reasons`;
    return this.baseService.get(url);
  }

  triggerMemberStatusRetrieval(): void {
    this.memberStatusEventEmitter.emit(true);
  }

  getMemberStatus() {
    const url = `${ENVIRONMENT.service.v2}/me?includes[]=member_status`;
    return this.baseService.get(url);
  }

  getFAInvestmentSettings() {
    const url = `${ENVIRONMENT.service.v2}/fa/me/investment-settings`;
    return this.baseService.get(url);
  }

  postFAInvestmentSettings(body) {
    const url = `${ENVIRONMENT.service.v2}/fa/me/investment-settings`;
    return this.baseService.post(url, body);
  }

  putFAInvestmentSettings(investmentSettingId, body) {
    const url = `${ENVIRONMENT.service.v2}/fa/me/investment-settings/${investmentSettingId}`;
    return this.baseService.put(url, body);
  }

  getUserHashCode() {
    const url = `${ENVIRONMENT.service.v2}/me/integration/ld/user`;
    return this.baseService.get(url);
  }

  getAutoInvestmentSettingList() {
    const url = `${ENVIRONMENT.service.v2}/me/auto_investment_settings?include_disabled=true&version=3`;
    return this.baseService.get(url);
  }

  // Get cbs rating, company age and max borrower exposure list value
  getAutoInvestmentDefaultSetting(): Observable<any> {
    const url = `${ENVIRONMENT.service.v2}/auto_investment_settings/default`;
    return this.baseService.get(url);
  }

  // Get loan type detail for AA validation & default value
  getAutoInvestmentValidationProps(memberCategoryCode: string) {
    let params = new HttpParams;
    if (memberCategoryCode === memberCategoryCodes.CAPITAL) {
      params = params.append('is_visible_to_capital_members', false);
    }
    const url = `${ENVIRONMENT.service.v2}/members/auto_investment_settings_validation_props?${params.toString()}`;
    return this.baseService.get(url);
  }

  addAutoInvestmentSetting(body: any) {
    const url = `${ENVIRONMENT.service.v2}/me/auto_investment_settings`;
    return this.baseService.post(url, body);
  }

  deleteAutoInvestmentSetting(id: number) {
    const url = `${ENVIRONMENT.service.v2}/me/auto_investment_settings/${id}`;
    return this.baseService.delete(url);
  }

  updateAutoInvestmentSettings(id: number, body: any) {
    const url = `${ENVIRONMENT.service.v2}/me/auto_investment_settings/${id}`;
    return this.baseService.put(url, body);
  }

  toggleAutoInvestmentActivation(id: number) {
    const url = `${ENVIRONMENT.service.v2}/me/auto_investment_settings/${id}/toggle_activation`;
    return this.baseService.put(url, {});
  }

  getBniVaNumber() {
    const url = `${ENVIRONMENT.service.v2}/me?attributes=bni_va_number`;
    return this.baseService.get(url);
  }

  reactivateMember(reason: string) {
    const url = `${ENVIRONMENT.service.v2}/me/reactivate/${reason}`;
    return this.baseService.put(url, {});
  }

  private mapCountryDetails(response: any): CountryDetailData {
    let result: CountryDetailData = null;
    if (response.data) {
      const data = response.data;
      result = <CountryDetailData>({
        id: data.id,
        code: data.code,
        name: data.name,
        withholding_tax: data.withholding_tax,
        is_gross_withholding_tax: data.is_gross_withholding_tax,
        investor_minimum_age: data.investor_minimum_age,
        is_exclusive_gst: data.is_exclusive_gst,
        gst: data.gst,
        rounding_adjustment_limit: data.rounding_adjustment_limit,
        auto_investment_threshold: data.auto_investment_threshold,
        deposit_settings: data.deposit_settings,
        withdrawal_settings: data.withdrawal_settings
      });
    }
    return result;
  }

  private filterCountriesList(response: any): any {
    let result = null;
    result = response.data;
    if (response.data) {
      let idArr = ['ZU', 'ZI'];
      result.data = response.data.sort(function (a, b) {
        return a.name.localeCompare(b.name)
      }).filter(item => !idArr.includes(item.code));
    }
    return result;
  }

  private mapLoginDetail(response: any): MemberLoginDetail {
    const data = response.data;
    let result = null;

    if (data) {
      result = <MemberLoginDetail>({
        activatedAt: data.activated_at,
        activationStepCode: null,
        assessment: null,
        countryId: data.country_code,
        fullName: data.fullname,
        id: data.id,
        isInstitutional: null,
        memberActivationStatusCode: null,
        memberStatusCode: null,
        memberEntityCode: data.member_entity_code,
        memberEntityTypeId: data.member_entity_type.id,
        memberTypeCode: null,
        memberTypeName: null,
        needsRDNOpening: data.needs_rdn_opening,
        referralCode: data.referral_code,
        referralId: data.referral_id,
        subaccounts: new Array<MemberSubaccount>(),
        userName: data.username,
        memberChangeRequestEntity: data.member_change_requests.entity,
        locale: data.locale,
        uuid: data.uuid,
      });
      if (data.activation_step) {
        result.activationStepCode = data.activation_step.code;
      }
      if (data.member_category_code) {
        result.memberCategoryCode = data.member_category_code;
      }
      if (data.member_entity_type) {
        result.memberEntityTypeId = data.member_entity_type.id;
        result.isInstitutional = data.member_entity_type.is_institutional;
      }
      if (data.member_type) {
        result.memberTypeName = data.member_type.name.toUpperCase();
        result.memberTypeCode = data.member_type.code;
      }
      if (data.member_activation_status) {
        result.memberActivationStatusCode = data.member_activation_status.code;
      }
      if (data.member_status) {
        result.memberStatusCode = data.member_status.code;
      }
      if (data.subaccounts) {
        result.subaccounts = data.subaccounts.map(subaccount => {
          return <MemberSubaccount>({
            accessLevel: subaccount.access_level,
            id: subaccount.id,
            userName: subaccount.username,
          });
        })
      }
      if (data.suitability_assessments) {
        const lastItem = data.suitability_assessments[data.suitability_assessments.length - 1];
        result.assessment = lastItem
          ? lastItem.type
          : null;
      }
    }

    return result;
  }

  private mapSubscriptionAgreementTemplate(response: any): Array<SubscriptionAgreementTemplate> {
    const result: Array<SubscriptionAgreementTemplate> = [];
    for (const item of response.data) {
      result.push(<SubscriptionAgreementTemplate>({
        code: item.code,
        grouping: item.grouping,
        input_type: item.input_type,
        name: item.name,
        order: item.order,
        required: item.required,
      }));
    }
    return result;
  }

  getMemberMasterData(memberTypeCode: string): Observable<any> {
    return this.baseService.get(
      ENVIRONMENT.service.member +
      ENVIRONMENT.service.public_identifier +
      '/members/' +
      memberTypeCode +
      '/references?country_id=' +
      CONFIGURATION.country_code,
    );
  }

  getSubscriptionAgreementTemplatesv1(): Observable<Array<SubscriptionAgreementTemplatev1>> {
    const url = ENVIRONMENT.service.member + '/subscription_agreement_templates/all/' + CONFIGURATION.country_code;
    return this.baseService.get(url).pipe(
      map(this.mapSubscriptionAgreementTemplatev1));
  }

  private mapSubscriptionAgreementTemplatev1(response: any): Array<SubscriptionAgreementTemplatev1> {
    const result: Array<any> = [];
    for (const item of response.data) {
      result.push(<SubscriptionAgreementTemplatev1>({
        id: item.id,
        countryId: item.countryId,
        label: item.label,
        displayedLabel: item.displayedLabel,
        prependedLabel: "",
        grouping: item.grouping,
        inputTypeId: item.inputType.id,
        inputTypeCode: item.inputType.code,
        inputTypeName: item.inputType.name,
        valueString: null,
        valueDecimal: null,
        valueBoolean: false,
        required: item.required,
        valid: true,
        hidden: item.grouping ? item.grouping.indexOf("_SUB") !== -1 : false,
        page: item.page
      }));
    }

    return result;
  }

  getMemberDetailv1(): Observable<any> {
    const url = ENVIRONMENT.service.member + '/members/any/single/simple/me';
    return this.baseService.get(url).pipe(
      map(this.mapMemberDetail));
  }

  private mapMemberDetail(response: any) {
    const data = response.data;
    let result = null;

    if (data) {
      result = <any>({
        id: data.id,
        memberTypeId: data.memberTypeId,
        memberEntityTypeId: data.memberEntityTypeId,
        memberStatusId: data.memberStatusId,
        memberActivationStatusId: data.memberActivationStatusId,
        userName: data.userName,
        firstName: data.firstName,
        lastName: data.lastName,
        fullName: data.fullname,
        icTypeId: data.icTypeId,
        icNumber: data.icNumber,
        passportNumber: data.passportNumber,
        passportExpiryDate: data.passportExpiryDate,
        taxCardTypeId: data.taxCardTypeId,
        taxCardNumber: data.taxCardNumber,
        phoneNumber: data.phoneNumber,
        mobilePhoneNumber: data.mobilePhoneNumber,
        faxNumber: data.faxNumber,
        address1: data.address1,
        address2: data.address2,
        countryId: data.countryId,
        residentialCountryId: data.residentialCountryId,
        stateId: data.stateId,
        cityId: data.cityId,
        district: data.district,
        area: data.area,
        areaId: data.areaId,
        areaCode: data.areaCode,
        zipCode: data.zipCode,
        unitNumber: data.unitNumber,
        unitFloor: data.unitFloor,
        bcaVaNumber: data.bcaVaNumber,
        birthDate: data.birthDate,
        birthPlace: data.birthPlace,
        genderId: data.genderId,
        referralId: data.referralId,
        referralCode: data.referralCode,
        virtualAccountNumber: data.virtualAccountNumber,
        citizenshipIsSame: data.citizenshipIsSame,
        citizenshipAddress1: data.citizenshipAddress1,
        citizenshipAddress2: data.citizenshipAddress2,
        citizenshipCountryId: data.citizenshipCountryId,
        citizenshipStateId: data.citizenshipStateId,
        citizenshipCityId: data.citizenshipCityId,
        citizenshipDistrict: data.citizenshipDistrict,
        citizenshipArea: data.citizenshipArea,
        citizenshipAreaCode: data.citizenshipAreaCode,
        citizenshipZipCode: data.citizenshipZipCode,
        citizenshipUnitNumber: data.citizenshipUnitNumber,
        citizenshipPermanentResident: data.citizenshipPermanentResident,
        citizenshipUnitFloor: data.citizenshipUnitFloor,
        isVip: data.isVip,
        companyIndustryId: data.companyIndustryId,
        companyIndustryOther: data.companyIndustryOther,
        companyEntityTypeId: data.companyEntityTypeId,
        companyName: data.companyName,
        companyRegistrationNumber: data.companyRegistrationNumber,
        companyPhoneNumber: data.companyPhoneNumber,
        companyEmail: data.companyEmail,
        companyFaxNumber: data.companyFaxNumber,
        companyAddress1: data.companyAddress1,
        companyAddress2: data.companyAddress2,
        companyCountryId: data.companyCountryId,
        companyStateId: data.companyStateId,
        companyCityId: data.companyCityId,
        companyDistrict: data.companyDistrict,
        companyArea: data.companyArea,
        companyAreaCode: data.companyAreaCode,
        companyZipCode: data.companyZipCode,
        companyUnitNumber: data.companyUnitNumber,
        companyUnitFloor: data.companyUnitFloor,
        companyBirthDate: data.companyBirthDate,
        companyTotalEmployeeId: data.companyTotalEmployeeId,
        companyCrimeStatus: data.companyCrimeStatus,
        companyCrimeDetail: data.companyCrimeDetail,
        companyEverBankrupt: data.companyEverBankrupt,
        companyOwnership: data.companyOwnership,
        companyAnnualRevenue: data.companyAnnualRevenue,
        companyAnnualNetIncome: data.companyAnnualNetIncome,
        companyCurrentDebt: data.companyCurrentDebt,
        companyCurrentInstallment: data.companyCurrentInstallment,
        activationStepId: data.activationStepId,
        activationStep: null,
        memberBankAccounts: [],
        memberCrowdfundingSettings: [],
        assessment: '1',
        subscriptionAgreementExists: data.agreementsList && data.agreementsList.length > 0,
        signupDate: data.createdAt,
        motherMaidenName: data.motherMaidenName,
        degreeCode: data.degreeCode,
        maritalStatusId: data.maritalStatusId,
        religionCode: data.religionCode,
        beneficialOwnerFullname: data.beneficialOwnerFullname,
        beneficialOwnerICNumber: data.beneficialOwnerICNumber,
        beneficialOwnerFullAddress: data.beneficialOwnerFullAddress,
        beneficialOwnerRelationship: data.beneficialOwnerRelationship,
        spouseFullName: data.spouseFullName,
        emergencyRelationship: data.emergencyRelationship,
        emergencyFullname: data.emergencyFullname,
        emergencyMobilePhoneNumber: data.emergencyMobilePhoneNumber,
        emergencyAddress1: data.emergencyAddress1,
        emergencyAreaId: data.emergencyAreaId,
        emergencyAreaCode: data.emergencyAreaCode,
        emergencyZipCode: data.emergencyZipCode,
        emergencyCountryId: data.emergencyCountryId,
        citizenshipAreaId: data.citizenshipAreaId,
        investBalance: data.investBalance,
        exposurePerBorrower: data.exposurePerBorrower,
        exposurePerIndustry: data.exposurePerIndustry,
        lastSignUpStepWeb: data.lastSignUpStepWeb,
      });
      if (data.memberType) {
        result.memberTypeCode = data.memberType.code;
        result.memberTypeName = data.memberType.name.toUpperCase();
      }
      if (data.memberEntityType) {
        result.memberEntityType = <any>({
          id: data.memberEntityType.id,
          name: data.memberEntityType.name,
          isInstitutional: data.memberEntityType.isInstitutional,
        });
      }
      if (data.memberStatus) {
        result.memberStatus = <any>({
          id: parseInt(data.memberStatus.id, 10),
          name: data.memberStatus.name,
        });
      }
      if (data.memberActivationStatus) {
        result.memberActivationStatus = <any>({
          id: data.memberActivationStatus.id,
          name: data.memberActivationStatus.name,
          code: data.memberActivationStatus.code,
        });
      }

      if (data.sgGovInfo && data.sgGovInfo.pass) {
        result.sgGovInfoPassData = data.sgGovInfo.pass;
      }

      for (const item of data.memberBankAccounts) {
        result.memberBankAccounts.push({
          id: item.id,
          memberId: data.id,
          bankId: item.bankId,
          bankName: "",
          bankOther: item.bankOther,
          bankAccountTypeId: item.bankAccountTypeId,
          // bankAccountTypeName: item.bankAccountType.name,
          branch: item.branch,
          address: item.address,
          swiftCode: item.swiftCode,
          currency: item.currency,
          number: item.number,
          name: item.name,
          isDefault: item.isDefault,
          isValid: true,
          pic: item.createdBy
        });
      }
      for (const item of data.memberCrowdfundingSettings) {
        result.memberCrowdfundingSettings.push(<any>({
          id: item.id,
          loanTypeId: item.loanTypeId,
          tenorTypeId: item.tenorTypeId,
          minimumTenor: item.minimumTenor,
          maximumTenor: item.maximumTenor,
          minimumInterestRate: item.minimumInterestRate,
          maximumInterestRate: item.maximumInterestRate,
          investOnAmountAllowed: item.investOnAmountAllowed,
          amount: item.amount,
          industries: item.industries.map(i => i.industryId).join(','),
          pic: item.createdBy
        }));
      }
      if (data.activationStep) {
        result.activationStep = <any>({
          id: data.activationStep.id,
          code: data.activationStep.code,
          name: data.activationStep.name
        });
      }
      for (const item of data.suitabilityAssessments) {
        result.assessment = item.type;
      }
      if (data.faMemberId) {
        result.faMemberId = data.faMemberId;
      }
      if (data.faMember) {
        result.faMember = data.faMember;
      }
    }

    return result;
  }

  createMemberSubscriptionAgreementv1(memberSubscriptionAgreements: Array<SubscriptionAgreementTemplatev1>) {
    const body = {
      list: memberSubscriptionAgreements
    };
    const url = ENVIRONMENT.service.member + '/member_subscription_agreements';
    return this.baseService.post(url, body);
  }

  addBankAccountv1(body: MemberBankAccountv1): Observable<any> {
    const url = ENVIRONMENT.service.member + '/member_bank_accounts';
    return this.baseService.post(url, body);
  }

  updateBankAccountsv1(memberBankAccounts: Array<MemberBankAccountv1>): Observable<any> {
    const url = ENVIRONMENT.service.member + '/member_bank_accounts/me';
    const body = {
      list: memberBankAccounts
    };
    return this.baseService.put(url, body);
  }

}


